import { useContext, useEffect, useState } from 'react';
import { SmartContext } from '../../../../library/Core/SmartContext';
import SearchCriteria from './SearchCriteria';
import GrievanceCardComponent from './GrievanceCard';
import StatusUpdateModal, { ModalAction } from './StatusUpdateModal';
import type { GrievanceCard, GrievanceStats } from '../services/InstituteAdminGrievanceService';
import InstituteAdminGrievanceService from '../services/InstituteAdminGrievanceService';
import GrievanceDetailsModal from '../shared/GrievanceDetailsModal';
import { Link } from 'react-router-dom';
import { allowPerformAction, displayActionNotAllowedAlert, getActionElementClass } from '../../../../library/Core/SmartFunctions';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const formatToday = () => {
    const d = new Date();
    return `${String(d.getDate()).padStart(2,'0')}-${MONTHS[d.getMonth()]}-${d.getFullYear()}`;
};

const Layout = () => {
    const { state, dispatch } = useContext(SmartContext);

    const stats: GrievanceStats     = state.data?.stats ?? { total: 0, critical: 0, pendingYourAction: 0, resolved: 0 };
    const grievances: GrievanceCard[] = state.data?.grievanceList ?? [];

    // Status Update Modal
    const [modalAction, setModalAction]           = useState<ModalAction | null>(null);
    const [selectedGrievance, setSelectedGrievance] = useState<GrievanceCard | null>(null);

    // Grievance Details Modal
    const [detailGrievanceId, setDetailGrievanceId] = useState<number | null>(null);

    useEffect(() => {
        const handler = (e: Event) => {
            const { id } = (e as CustomEvent<{ id: number }>).detail;
            setDetailGrievanceId(id);
        };
        window.addEventListener('openGrievanceDetails', handler);
        return () => window.removeEventListener('openGrievanceDetails', handler);
    }, []);

    const handleCardAction = (action: ModalAction, grievance: GrievanceCard) => {
        if (!allowPerformAction()) {
            displayActionNotAllowedAlert();
            return;
        }
        setModalAction(action);
        setSelectedGrievance(grievance);
    };

    const handleViewDetails = (grievance: GrievanceCard) => {
        window.dispatchEvent(new CustomEvent('openGrievanceDetails', { detail: { id: grievance.id } }));
    };

    const handleStatusUpdated = (grievanceId: number, newStatus: string) => {
        const oldStatus = grievances.find((g) => g.id === grievanceId)?.status;
        const updated = grievances.map((g) =>
            g.id === grievanceId ? { ...g, status: newStatus, updatedAt: formatToday() } : g,
        );
        dispatch({ type: 'CONTROL_VALUE_CHANGE', payload: { dataKey: 'grievanceList', value: updated, errorMessages: [] } });
        // Update stat card counts optimistically
        const updatedStats = { ...stats };
        if (oldStatus === 'new' && newStatus !== 'new') updatedStats.pendingYourAction = Math.max(0, updatedStats.pendingYourAction - 1);
        if (oldStatus !== 'new' && newStatus === 'new') updatedStats.pendingYourAction += 1;
        if (newStatus === 'resolved' && oldStatus !== 'resolved') updatedStats.resolved += 1;
        if (oldStatus === 'resolved' && newStatus !== 'resolved') updatedStats.resolved = Math.max(0, updatedStats.resolved - 1);
        dispatch({ type: 'CONTROL_VALUE_CHANGE', payload: { dataKey: 'stats', value: updatedStats, errorMessages: [] } });
        setModalAction(null);
        setSelectedGrievance(null);
    };

    const handleModalClose = () => {
        setModalAction(null);
        setSelectedGrievance(null);
    };

    const handleExport = () => {
        if (!allowPerformAction()) {
            displayActionNotAllowedAlert();
            return;
        }
        InstituteAdminGrievanceService.exportCSV(grievances);
    };

    const isLoading   = state.flags?.isDataLoading;
    const hasFilters  = Object.values(state.data?.searchCriteria ?? {}).some(Boolean);

    return (
        <div className="main flex-1">
            <section className="alumni_directory_details container-fluid">
                <div className="max-1250 pt-3 pt-sm-5 px-lg-0 pb-5 overflow-hidden mx-auto">
                    <div className="alumni_statistics">

                        {/* ── Page header ─────────────────────────────── */}
                        <div className="row justify-content-between align-items-center mb-4 g-2">
                            <div className="col">
                                <h2 className="font-26 font-600 mb-0">Grievance Management</h2>
                                <p className="font-18 mb-0">
                                    Alumni concerns routed to your institution by Univariety — requiring your attention
                                </p>
                            </div>
                            <div className="col-auto">
                                <button
                                    onClick={handleExport}
                                    className={`btn btn-skyblue w-auto height-40 d-inline-flex text-white font-14 text-decoration-none px-3 ${getActionElementClass()}`}
                                >
                                    <i className="bi bi-box-arrow-up-right font-16 me-1"></i>
                                    <span>Export</span>
                                </button>
                            </div>

                                                        {/* Add New Grievance */}
                                                        <div className={`col-auto ${getActionElementClass()}`}>
                                                            <Link to="/support/add-grievance" className="btn btn-green w-auto height-40 d-inline-flex text-white font-14 text-decoration-none px-3"
                                                                onClick={(e) => {
                                                                    if (!allowPerformAction()) {
                                                                        e.preventDefault();
                                                                        displayActionNotAllowedAlert();
                                                                    }
                                                                }}
                                                            >
                                                                <i className="icon-plus circle"></i>
                                                                <span>Add New Grievance</span>
                                                            </Link>
                                                        </div>
                        </div>

                        <div className="ActiveListings_present">

                            {/* ── Stat cards ──────────────────────────── */}
                            <div className="x-scroll pt-1 mb-3">
                                <div className="min-wd-767">
                                    <div className="row g-3 mx-0">
                                        <div className="col">
                                            <div className="bg-white p-4 custom-shadow h-100 rounded-4">
                                                <h3 className="font-24 font-600 mb-0 text-primary-emphasis">{stats.total}</h3>
                                                <p className="font-14 font-500 text-gray-64 mb-0 text-primary-emphasis">Total Grievances</p>
                                            </div>
                                        </div>
                                        <div className="col">
                                            <div className="bg-danger bg-opacity-10 border border-danger-subtle p-4 h-100 rounded-4">
                                                <h3 className="font-24 font-600 mb-0 text-danger">{stats.critical}</h3>
                                                <p className="font-14 font-500 mb-0 text-danger">Critical Priority</p>
                                            </div>
                                        </div>
                                        <div className="col">
                                            <div className="bg-warning bg-opacity-10 border border-warning border-opacity-25 p-4 h-100 rounded-4">
                                                <h3 className="font-24 font-600 mb-0 text-warning-emphasis">{stats.pendingYourAction}</h3>
                                                <p className="font-14 font-500 text-warning-emphasis mb-0">Pending Your Action</p>
                                            </div>
                                        </div>
                                        <div className="col">
                                            <div className="bg-success bg-opacity-10 border border-success border-opacity-25 p-4 h-100 rounded-4">
                                                <h3 className="font-24 font-600 mb-0 text-success">{stats.resolved}</h3>
                                                <p className="font-14 font-500 mb-0 text-success">Resolved</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* ── Filters + info banner ────────────────── */}
                            <SearchCriteria />

                            {/* ── Results count ────────────────────────── */}
                            <div className="d-flex flex-wrap align-items-center mb-3">
                                <h3 className="font-16 font-400 mb-0 me-2 text-secondary">
                                    {grievances.length} {grievances.length === 1 ? 'grievance' : 'grievances'}
                                </h3>
                            </div>

                            {/* ── Card listing ─────────────────────────── */}
                            {isLoading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading…</span>
                                    </div>
                                </div>
                            ) : grievances.length === 0 ? (
                                <div className="text-center py-5">
                                    <img
                                        src="https://s3.ap-south-1.amazonaws.com/media.univariety.com/userreactapp/images/no-result-img-new.svg"
                                        width="220"
                                        className="mb-3"
                                        alt="No grievances"
                                    />
                                    <p className="font-18 text-muted">
                                        {hasFilters
                                            ? 'No grievances match your current filters. Use Clear to reset.'
                                            : 'No grievances have been pushed to your institute yet.'}
                                    </p>
                                </div>
                            ) : (
                                <div className="grievances-block">
                                    {grievances.map((g) => (
                                        <GrievanceCardComponent
                                            key={g.id}
                                            grievance={g}
                                            onViewDetails={() => handleViewDetails(g)}
                                            onMarkResolved={() => handleCardAction('markResolved', g)}
                                            onAddResponse={() => handleCardAction('addResponse', g)}
                                            onDiscard={() => handleCardAction('discard', g)}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* Status Update Modal */}
            {modalAction && selectedGrievance && (
                <StatusUpdateModal
                    action={modalAction}
                    grievance={selectedGrievance}
                    onSuccess={handleStatusUpdated}
                    onClose={handleModalClose}
                />
            )}

            {/* Grievance Details Modal */}
            <GrievanceDetailsModal
                grievanceId={detailGrievanceId}
                onClose={() => setDetailGrievanceId(null)}
                onStatusUpdated={(id, newStatus) => {
                    const oldStatus = grievances.find((g) => g.id === id)?.status;
                    const updated = grievances.map((g) =>
                        g.id === id ? { ...g, status: newStatus, updatedAt: formatToday() } : g,
                    );
                    dispatch({ type: 'CONTROL_VALUE_CHANGE', payload: { dataKey: 'grievanceList', value: updated, errorMessages: [] } });
                    const updatedStats = { ...stats };
                    if (oldStatus === 'new' && newStatus !== 'new') updatedStats.pendingYourAction = Math.max(0, updatedStats.pendingYourAction - 1);
                    if (oldStatus !== 'new' && newStatus === 'new') updatedStats.pendingYourAction += 1;
                    if (newStatus === 'resolved' && oldStatus !== 'resolved') updatedStats.resolved += 1;
                    if (oldStatus === 'resolved' && newStatus !== 'resolved') updatedStats.resolved = Math.max(0, updatedStats.resolved - 1);
                    dispatch({ type: 'CONTROL_VALUE_CHANGE', payload: { dataKey: 'stats', value: updatedStats, errorMessages: [] } });
                }}
            />
        </div>
    );
};

export default Layout;
